import { fabric } from 'fabric';
import { CustomControlsIcons } from '../canvas-custom-config-types';
import { renderCustomControls } from '../utils/render-custom-controls';
import { setIsDeleteConfirmationDialogOpen, setIsRotationDrawerOpen } from '../../../context/app-context';
import { renderIcon } from './render-icon';

export const setCustomControls = (
  { _fabric }: any,
  { deleteIcon, scaleIcon, circleIcon, rectangleIcon, rotateIcon }: CustomControlsIcons,
  appDispatch: React.Dispatch<any>,
  isUK: boolean | undefined,
  isNewToolbarEnabled = false,
) => {
  const onDeleteObject = (): boolean => {
    setIsDeleteConfirmationDialogOpen(appDispatch);
    return true;
  };

  const onRotateObject = (): boolean => {
    // @todo ensure implementation
    setIsRotationDrawerOpen(appDispatch);
    return true;
  };

  _fabric.Object.prototype.drawControls = function (ctx) {
    let coords;
    this.setCoords();
    this.forEachControl(function (control, key, fabricObject) {
      coords = fabricObject.oCoords[`${key}`];
      const icons = {
        mr: rectangleIcon,
        ml: rectangleIcon,
        tr: circleIcon,
        bl: circleIcon,
        mtr: rotateIcon,
        deleteControl: deleteIcon,
        tl: circleIcon,
        br: circleIcon,
      };
      renderCustomControls(icons, key, ctx, coords.x, coords.y, fabricObject, control, isNewToolbarEnabled);
    });
    ctx.restore();
  };

  _fabric.Object.prototype.set({
    borderColor: isUK ? '#333333' : '#604099',
    // PRCM-10897: match the HTML overlay outline visually. Overlay is
    // `outline: 2px dashed <color>` with border-radius: 4px. The rounded
    // corners come from the drawBorders override below; here we just set
    // the 2px stroke weight and CSS-`dashed`-like [5,3] dash pattern.
    borderScaleFactor: 2,
    borderDashArray: [5, 3],
  });

  // PRCM-10897: override fabric's drawBorders so the selection border on
  // text objects renders with rounded corners (border-radius: 4px) matching
  // the HTML overlay outline. Fabric ships strokeRect only — no rounded
  // support — so we replace with a rounded-rect path. The connecting-line
  // logic for controls (withConnection) is preserved verbatim so trash/
  // rotate anchors still render correctly.
  const roundedDrawBorders = function (this: any, ctx: CanvasRenderingContext2D, styleOverride: any) {
    styleOverride = styleOverride || {};
    const wh = this._calculateCurrentDimensions();
    const strokeWidth = this.borderScaleFactor;
    const width = wh.x + strokeWidth;
    const height = wh.y + strokeWidth;
    const radius = Math.min(4, width / 2, height / 2);
    const hasControls = typeof styleOverride.hasControls !== 'undefined' ? styleOverride.hasControls : this.hasControls;
    let shouldStroke = false;

    ctx.save();
    ctx.strokeStyle = styleOverride.borderColor || this.borderColor;
    this._setLineDash(ctx, styleOverride.borderDashArray || this.borderDashArray);
    ctx.lineWidth = strokeWidth;
    ctx.beginPath();
    const left = -width / 2;
    const top = -height / 2;
    const right = left + width;
    const bottom = top + height;
    ctx.moveTo(left + radius, top);
    ctx.lineTo(right - radius, top);
    ctx.quadraticCurveTo(right, top, right, top + radius);
    ctx.lineTo(right, bottom - radius);
    ctx.quadraticCurveTo(right, bottom, right - radius, bottom);
    ctx.lineTo(left + radius, bottom);
    ctx.quadraticCurveTo(left, bottom, left, bottom - radius);
    ctx.lineTo(left, top + radius);
    ctx.quadraticCurveTo(left, top, left + radius, top);
    ctx.closePath();
    ctx.stroke();

    if (hasControls) {
      ctx.beginPath();
      this.forEachControl(function (control: any, key: string, fabricObject: any) {
        if (control.withConnection && control.getVisibility(fabricObject, key)) {
          shouldStroke = true;
          ctx.moveTo(control.x * width, control.y * height);
          ctx.lineTo(control.x * width + control.offsetX, control.y * height + control.offsetY);
        }
      });
      if (shouldStroke) {
        ctx.stroke();
      }
    }
    ctx.restore();
    return this;
  };

  if (_fabric.Textbox?.prototype) {
    _fabric.Textbox.prototype.drawBorders = roundedDrawBorders;
  }
  if (_fabric.IText?.prototype) {
    _fabric.IText.prototype.drawBorders = roundedDrawBorders;
  }
  if (_fabric.Text?.prototype) {
    _fabric.Text.prototype.drawBorders = roundedDrawBorders;
  }

  _fabric.Textbox.prototype.controls.deleteControl = new fabric.Control({
    x: 0,
    y: -0.5,
    offsetY: -40,
    withConnection: true,
    cursorStyle: 'pointer',
    mouseUpHandler: onDeleteObject,
    render: renderIcon(deleteIcon),
    actionName: 'delete',
  });

  _fabric.Textbox.prototype.controls.rotateControl = new fabric.Control({
    x: 0,
    y: -0.5,
    offsetY: -40,
    withConnection: true,
    cursorStyle: 'pointer',
    mouseUpHandler: onRotateObject,
    actionHandler: (eventData, transformData: fabric.Transform, x, y) => {
      (transformData.originX as 'left' | 'right' | 'center') = 'center';
      (transformData.originY as 'left' | 'right' | 'center') = 'center';
      return fabric.Object.prototype.controls.mtr.actionHandler(eventData, transformData, x, y);
    },
    render: renderIcon(rotateIcon),
  });

  // Hide default controls
  _fabric.Textbox.prototype.setControlsVisibility({
    mr: true,
    mt: false,
    ml: true,
    bl: true,
    mtr: true,
    mb: false,
    tr: true,
    tl: true,
    rotateControl: false,
  });

  // Assign circle icon renderers to Textbox corner controls so they always render regardless of
  // shouldShowResizeControls checks in renderTextControl.
  const textboxControls = _fabric.Textbox?.prototype?.controls;
  const objectPrototypeControls = _fabric.Object?.prototype?.controls;

  if (textboxControls && objectPrototypeControls) {
    const textCornerIconSize = 12;
    const textSideWidth = 12;
    const textSideHeight = 24;
    (['tl', 'tr', 'bl', 'br'] as const).forEach((corner) => {
      if (objectPrototypeControls[`${corner}`]) {
        textboxControls[`${corner}`] = new fabric.Control({
          ...objectPrototypeControls[`${corner}`],
          render: renderIcon(circleIcon, textCornerIconSize),
        });
      }
    });

    (['ml', 'mr'] as const).forEach((side) => {
      if (objectPrototypeControls[`${side}`]) {
        textboxControls[`${side}`] = new fabric.Control({
          ...objectPrototypeControls[`${side}`],
          render: renderIcon(rectangleIcon, textSideWidth, textSideHeight),
        });
      }
    });
  }

  // Assign custom icon renderers to Image controls so they survive drawControls overrides.
  // New Control instances avoid mutating shared Object.prototype control references.
  const imageControls = _fabric.Image?.prototype?.controls;
  const objectControls = _fabric.Object?.prototype?.controls;

  if (imageControls && objectControls) {
    if (objectControls.mtr) {
      imageControls.mtr = new fabric.Control({
        ...objectControls.mtr,
        render: renderIcon(rotateIcon),
      });
    }

    const cornerIconSize = 12;

    if (objectControls.tl) {
      imageControls.tl = new fabric.Control({
        ...objectControls.tl,
        render: renderIcon(circleIcon, cornerIconSize),
      });
    }

    if (objectControls.tr) {
      imageControls.tr = new fabric.Control({
        ...objectControls.tr,
        render: renderIcon(circleIcon, cornerIconSize),
      });
    }

    if (objectControls.bl) {
      imageControls.bl = new fabric.Control({
        ...objectControls.bl,
        render: renderIcon(circleIcon, cornerIconSize),
      });
    }

    if (objectControls.br) {
      imageControls.br = new fabric.Control({
        ...objectControls.br,
        render: renderIcon(circleIcon, cornerIconSize),
      });
    }
  }
};
